import { useState } from 'react';
import { Link } from 'wouter';
import { ArrowLeft, Inbox } from 'lucide-react';
import { useAdminAgentDecisions } from '../api/useAdminOps';
import { AdminAPI } from '@shared/types/api';
import DecisionCard from '../components/ops/DecisionCard';
import { RefreshButton, eventLabel } from '../components/ops/opsUi';

// Global decision inbox: everything the automation needs a human for.
// Pending first (nearest due date), then resolved newest-first — so the
// decisions you just answered are at the top, not buried under old ones.
// See docs/EVENT_OPS_AUTOMATION.md §7.4.

const FILTERS = [
  { key: undefined, label: 'All' },
  { key: 'pending' as const, label: 'Pending' },
  { key: 'approved' as const, label: 'Approved' },
  { key: 'rejected' as const, label: 'Rejected' },
  { key: 'answered' as const, label: 'Answered' },
  { key: 'expired' as const, label: 'Expired' },
];

export default function AdminOpsDecisions() {
  const [status, setStatus] = useState<(typeof FILTERS)[number]['key']>('pending');
  const { data, isLoading, isFetching, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAdminAgentDecisions(status ? { status } : {});

  const decisions = data?.pages.flatMap((p) => p.data?.decisions ?? []) ?? [];
  // Contexts arrive per page, keyed by decision id; merge across pages so a
  // card fetched on page 2 still finds its resolved tables.
  const contexts: Record<string, AdminAPI.DecisionContext> = Object.assign(
    {},
    ...(data?.pages.map((p) => p.data?.contexts ?? {}) ?? [])
  );
  const pendingCount = data?.pages[0]?.data?.pendingCount ?? 0;

  // One inbox box per (event × neighborhood): consecutive runs' questions for
  // the same scope live together instead of stacking separate cards. Group
  // order follows the API's sort (nearest-due pending, then newest resolved).
  const groups: {
    key: string;
    eventTimeId: string;
    neighborhoodName: string | null;
    decisions: typeof decisions;
  }[] = [];
  const groupByKey = new Map<string, (typeof groups)[number]>();
  for (const d of decisions) {
    const key = `${d.eventTimeId}|${d.neighborhoodId ?? 'event'}`;
    let group = groupByKey.get(key);
    if (!group) {
      group = {
        key,
        eventTimeId: d.eventTimeId,
        neighborhoodName: d.neighborhoodName ?? (d.neighborhoodId === null ? null : d.neighborhoodId),
        decisions: [],
      };
      groupByKey.set(key, group);
      groups.push(group);
    }
    group.decisions.push(d);
  }

  return (
    <>
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-4">
          <Link href="/ops" className="text-dark-muted hover:text-primary-500">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Inbox className="w-5 h-5 text-primary-500" />
          <h1 className="text-xl font-bold">Decision inbox</h1>
          <RefreshButton onClick={() => refetch()} isFetching={isFetching} />
          {pendingCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold">
              {pendingCount}
            </span>
          )}
        </div>

        <div className="flex gap-1.5 mb-4 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.label}
              onClick={() => setStatus(f.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium border ${
                status === f.key
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600'
                  : 'border-neutral-300 dark:border-neutral-600 text-dark-muted hover:border-primary-400'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {isLoading && <p className="text-sm text-dark-muted">Loading…</p>}
        {!isLoading && decisions.length === 0 && (
          <p className="text-sm text-dark-muted">
            {status === 'pending' ? 'Nothing needs a human right now. 🎉' : 'No decisions.'}
          </p>
        )}

        <div className="space-y-3">
          {groups.map((g) => (
            <div
              key={g.key}
              className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50/60 dark:bg-neutral-800/40 overflow-hidden"
            >
              <div className="flex items-center gap-2 px-3 py-1.5 border-b border-neutral-200 dark:border-neutral-700 flex-wrap">
                <span className="text-sm font-semibold">
                  {g.neighborhoodName ?? 'Event-wide'}
                </span>
                <span className="text-[11px] text-dark-muted px-1.5 py-0.5 rounded bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700">
                  {eventLabel(g.eventTimeId)}
                </span>
                {g.decisions.length > 1 && (
                  <span className="text-[11px] text-dark-muted">
                    {g.decisions.length} decisions
                  </span>
                )}
                <Link
                  href={`/matching/${g.eventTimeId}${
                    g.neighborhoodName
                      ? `?neighborhood=${encodeURIComponent(g.neighborhoodName)}`
                      : ''
                  }`}
                  className="ml-auto text-[11px] text-dark-muted hover:text-primary-500"
                >
                  Matching ↗
                </Link>
              </div>
              <div className="p-2 space-y-2">
                {g.decisions.map((d) => (
                  <DecisionCard key={d.id} decision={d} context={contexts[d.id]} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {hasNextPage && (
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="mt-4 text-sm text-primary-500 font-medium hover:underline disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    </>
  );
}
